/**
 * useProjectViews.jsx
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { StandardColumnIds } from "../helpers/columnUtils";
import { ProjectViewType, default as ProjectViewUtils } from "../ProjectViews/projectViewUtils";

const useProjectViews = (project) => {
  const [activePanelData, setActivePanelData] = useState({});
  const [activeTabIndex, setActiveTabIndex] = useState();
  const [viewSettings, setViewSettings] = useState({});

  const projectTabs = useMemo(() => (
    ProjectViewUtils.getProjectTabsData(project)
  ), [project]);

  const isActivePanel = useCallback((type) => (type === activePanelData.type), [activePanelData]);

  /**
   * Standard columns which are selectable in task views.
   * 
   * NOTE: Do not include TASK_NAME here. It is a special column that is always included.
   */
  const standardColumnIds = Object.freeze([
    StandardColumnIds.START_DATE,
    StandardColumnIds.END_DATE,
    StandardColumnIds.ASSIGNED_TO,
    StandardColumnIds.TIME_REPORT,
  ]);

  const parseViewSettings = (jsonSettings, fallbackSettings = {}) => {
    try {
      return JSON.parse(jsonSettings);
    }
    catch (error) {
      console.warn("** failed to parse settings.\n", error);
      return fallbackSettings;
    }
  };

  const createPanelProps = useCallback((type) => {
    const isActive = isActivePanel(type);
    return {
      ariaId: ProjectViewUtils.getAriaTabPanelId(type),
      ariaLabelledBy:
        isActive
          ? activePanelData.ariaLabelledBy
          : undefined,
      hidden: !isActive,
    };
  }, [isActivePanel, activePanelData]);

  const getViewSettings = useCallback((viewData) => {
    if (viewData.type === ProjectViewType.Tasks) {
      const { fieldIds } = parseViewSettings(viewData.jsonViewSettings);
      const isValid = Array.isArray(fieldIds);

      const viewColumnIds = isValid
        ? fieldIds
        : [...standardColumnIds, ...project?.projectFields.map(n => n.fieldId)];

      return { viewColumnIds };
    }
    else if (viewData.type === ProjectViewType.Kanban) {
      const { fieldId } = parseViewSettings(viewData.jsonViewSettings);
      const isValid = (Number.isSafeInteger(fieldId) && fieldId > 0);

      const kanbanFieldId = isValid
        ? fieldId
        : undefined;

      return { kanbanFieldId };
    }
    else {
      return {};
    }
  }, [project?.projectFields, standardColumnIds]);

  const onSelectView = useCallback((index, viewId) => {
    if (!Number.isSafeInteger(index)) return;

    const viewData = projectTabs[index];
    const ariaLabelledBy = ProjectViewUtils.getAriaTabId(viewData.viewId);

    setActiveTabIndex(index);
    setActivePanelData({ ...viewData, ariaLabelledBy });

    const settings = getViewSettings(viewData);

    setViewSettings(settings);
  }, [activeTabIndex, projectTabs, getViewSettings]);

  const resetProjectViews = useCallback(() => {
    setActivePanelData({});
    setActiveTabIndex();
    setViewSettings({});
  }, []);

  useEffect(() => {
    // Check if the project exists, there is no valid active tab,
    // and the project has defined tabs.
    const hasTabs = Array.isArray(projectTabs) && projectTabs.length > 0;
    const noActiveTab = !Number.isFinite(activeTabIndex);

    if (project && noActiveTab && hasTabs) {
      onSelectView(0); // Automatically select the first tab (default tab)
    }
  }, [project, projectTabs, activeTabIndex, onSelectView]);

  useEffect(() => {
    onSelectView(activeTabIndex);
  }, [project?.projectFields]);

  return {
    activeTabIndex,
    createPanelProps,
    isActivePanel,
    onSelectView,
    projectTabs,
    resetProjectViews,
    viewSettings,
  };
};

export default useProjectViews;
